// components/shift_table_daily_show.jsx
import React, { useState, useContext, useEffect, Fragment, useMemo } from 'react';
import { AuthContext } from '../contexts/auth';
import { Dialog, Transition } from '@headlessui/react';
import { PencilSquareIcon, TrashIcon, BriefcaseIcon, EyeSlashIcon, ClockIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

import { deleteTimeSlotCompany, updateWorkingHoursCompany, deleteWorkingHours } from '../services/api_working_hours';
import EditTimeSlot from './edit_time_slot';
import AddNewTimeSlot from './add_new_time_slot';
import Empty from './empty';
import Fetching from './fetching';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CustomTooltipComponent from './custom_tooltip_component';
import { formatTime } from "../utils/timeslots";
import { getTimeSlotsDuration, formatHoursAndMinutes } from '../utils/workTimeCount';

const getOuLabel = (wh) => wh?.ou_name || wh?.ouName || wh?.ou?.name || "";

// ANTEPRIMA TURNI GIORNALIERI SALVATI

export default function ShiftTableDailyShow({
  workingHours = [],
  whCount,
  loading = false,
  selectedUnit = null,
  allowHiddenBreaks = false,
  onUpdateWorkingHours,
  onDeleteShift,
  onDeleteTimeSlot,
}) {
  const { token } = useContext(AuthContext);

  const [workingHoursData, setWorkingHoursData] = useState(Array.isArray(workingHours) ? workingHours : []);

  const [editingShiftId, setEditingShiftId] = useState(null);

  const [editTimeSlot, setEditTimeSlot] = useState(null);
  const [editWhIndex, setEditWhIndex] = useState(null);
  const [workingHourId, setWorkingHourId] = useState(null);

  const [addTimeSlot, setAddTimeSlot] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const [isEditingName, setIsEditingName] = useState({});
  const [newNameWh, setNewNameWh] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setWorkingHoursData(Array.isArray(workingHours) ? workingHours : []);
    // chiudo eventuali edit aperti se cambia la lista (es: filtro/ricerca)
    setEditingShiftId(null);
    setEditTimeSlot(null);
    setOpenEditDialog(false);
    setAddTimeSlot(false);
    setOpenCreateDialog(false);
  }, [workingHours]);

  const sorted = useMemo(() => {
    return [...(workingHoursData || [])].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [workingHoursData]);

  const handleDeleteShift = async (id) => {
    const res = await Swal.fire({
      icon: "warning",
      text: "Il turno giornaliero verrà eliminato anche dai turni settimanali nei quali è stato assegnato. Sei sicuro di voler procedere?",
      showCancelButton: true,
      confirmButtonText: "Sì",
      cancelButtonText: "Annulla",
      cancelButtonColor: "#d1d5db",
    });

    if (!res.isConfirmed) return;

    try {
      await deleteWorkingHours(token, id);

      const next = (workingHoursData || []).filter((x) => x.id !== id);
      setWorkingHoursData(next);
      onUpdateWorkingHours?.(next);
      onDeleteShift?.(id);

      Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 1200 });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'eliminazione del turno" });
    }
  };
  
  const handleDeleteTimeSlot = async (slotId) => {
    const res = await Swal.fire({
      icon: "warning",
      text: "La fascia oraria verrà eliminata anche dai turni settimanali nei quali è stato assegnato il turno giornaliero. Sei sicuro di voler procedere?",
      showCancelButton: true,
      confirmButtonText: "Sì",
      cancelButtonText: "Annulla",
      cancelButtonColor: "#d1d5db",
    });

    if (!res.isConfirmed) return;

    try {
      await deleteTimeSlotCompany(token, slotId);

      const next = (workingHoursData || []).map((wh) => {
        if (!(wh?.time_slots || []).some((s) => s.id === slotId)) return wh;
        return { ...wh, time_slots: (wh.time_slots || []).filter((s) => s.id !== slotId) };
      });

      setWorkingHoursData(next);
      onUpdateWorkingHours?.(next);
      onDeleteTimeSlot?.(slotId);

      Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 1200 });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'eliminazione della fascia oraria" });
    }
  };

  const handleEditTimeSlot = (timeSlot, whIndex, whId) => {
    if (addTimeSlot) {
      Swal.fire({ icon: "info", text: "Chiudi prima la creazione della nuova fascia oraria." });
      return;
    }
    setEditTimeSlot([timeSlot]);
    setEditWhIndex(whIndex);
    setWorkingHourId(whId);
    setOpenEditDialog(true);
  };

  const handleAddTimeSlot = (whId) => {
    if (editTimeSlot) {
      Swal.fire({ icon: "info", text: "Concludi prima l'operazione di modifica." });
      return;
    }
    setWorkingHourId(whId);
    setAddTimeSlot(true);
    setOpenCreateDialog(true);
  };

  const handleCreateTimeSlot = (newTimeSlot) => {
    const next = (workingHoursData || []).map((wh) => {
      if (wh.id !== newTimeSlot.object_id) return wh;
      return { ...wh, time_slots: [...(wh.time_slots || []), newTimeSlot] };
    });
    setWorkingHoursData(next);
    onUpdateWorkingHours?.(next);
  };
  
  const handleUpdatedTimeSlot = (updatedTimeSlot) => {
    const next = (workingHoursData || []).map((wh) => ({
      ...wh,
      time_slots: (wh.time_slots || []).map((ts) => (ts.id === updatedTimeSlot.id ? updatedTimeSlot : ts)),
    }));
    setWorkingHoursData(next);
    onUpdateWorkingHours?.(next);
  };

  const handleEditNameClick = (idx, name) => {
    setNewNameWh(name || "");
    setIsEditingName((p) => ({ ...p, [idx]: true }));
  };

  const handleSaveNameClick = async (idx) => {
    const wh = sorted[idx];
    if (!wh?.id) return;

    try {
      setIsSaving(true);
      await updateWorkingHoursCompany(token, wh.id, newNameWh);

      const next = (workingHoursData || []).map((x) => (x.id === wh.id ? { ...x, name: newNameWh } : x));
      setWorkingHoursData(next);
      onUpdateWorkingHours?.(next);

      setIsEditingName((p) => ({ ...p, [idx]: false }));
      Swal.fire({ position: "center", icon: "success", title: "Salvato!", showConfirmButton: false, timer: 1200 });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'aggiornamento" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelNameClick = (idx) => {
    setIsEditingName((p) => ({ ...p, [idx]: false }));
    setNewNameWh("");
  };

  if (loading) {
    return (
      <div className="min-w-full text-center text-sm font-medium text-gray-600 py-5">
        <Fetching />
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="text-center text-sm text-gray-600 font-medium py-10">
        <Empty />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 pb-4 sticky bg-white md:top-[237px] lg:top[230px]" style={{ zIndex: '20' }}>
        <div className="min-w-0">
          <div className="text-indigo-700 font-semibold text-sm uppercase">Turni giornalieri</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {selectedUnit?.name
              ? `Unità selezionata: ${selectedUnit.name}`
              : "Turni generici (non collegati ad alcuna unità)"}
          </div>
        </div>

        <div className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600">
          <ClockIcon className="h-4 w-4" />
          {whCount} turn{whCount === 1 ? "o" : "i"}
        </div>
      </div>

      {/* lista card */}
      <div className="space-y-3">
        {sorted.map((wh, idx) => {
          const total = getTimeSlotsDuration(wh?.time_slots || []);
          const duration = formatHoursAndMinutes(total);

          return (
            <div
              key={wh.id || idx}
              className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
            >
              {/* card header */}
              <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-100">
                <div className="min-w-0">
                  {!isEditingName[idx] ? (
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-gray-900 truncate capitalize">
                        {wh.name}
                      </div>

                      {editingShiftId === wh.id && (
                        <CustomTooltipComponent title="Modifica nome" placement="right" arrow>
                          <button
                            type="button"
                            onClick={() => handleEditNameClick(idx, wh.name)}
                            className="rounded-md p-1 text-indigo-700 hover:bg-indigo-50"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </CustomTooltipComponent>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <input
                        value={newNameWh}
                        onChange={(e) => setNewNameWh(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isSaving}
                          onClick={() => handleSaveNameClick(idx)}
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                        >
                          {isSaving ? "Salvo..." : "Salva"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelNameClick(idx)}
                          className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  {/* badges */}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {!!duration && (
                      <span className="rounded-full bg-gray-50 px-2 py-1 font-semibold text-gray-700 border border-gray-400/50">
                        Totale: {duration}
                      </span>
                    )}
                    {!!wh?.ou && (
                      <span className="rounded-full bg-indigo-50 px-2 py-1 font-semibold text-indigo-700 border border-indigo-100 truncate w-auto max-w-24">
                        {getOuLabel(wh) || ""}
                      </span>
                    )}
                  </div>
                </div>

                {/* actions */}
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingShiftId(editingShiftId === wh.id ? null : wh.id)}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${
                      editingShiftId === wh.id
                        ? "bg-gray-300 text-gray-700 border-gray-300 hover:bg-gray-200"
                        : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-500"
                    }`}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {editingShiftId === wh.id ? "CHIUDI" : "MODIFICA"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteShift(wh.id)}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold bg-white text-red-600 border border-gray-200 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <TrashIcon className="h-4 w-4" />
                    ELIMINA
                  </button>
                </div>
              </div>

              {/* slots table */}
              <div className="p-4">
                {editingShiftId === wh.id && (
                  <div className="mb-2 w-full rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                    <span className="font-semibold">Attenzione:</span> se questo turno giornaliero è assegnato all'interno di un turno {" "}
                    <b>settimanale</b>, eventuali modifiche agli orari aggiorneranno automaticamente l'orario settimanale.
                  </div>
                )}
                <table className="w-full table-fixed border border-gray-200 rounded-lg shadow text-sm">
                  <thead className="bg-indigo-50 text-center">
                    <tr>
                      <th className="px-4 py-2 text-gray-600 w-[40%]">
                        <BriefcaseIcon className="w-4 h-4 text-indigo-700 me-1 inline-block" />{" "}
                        Fascia Oraria
                      </th>
                      <th className="px-4 py-2 text-gray-600 w-[40%]">
                        <LocalBarIcon
                          style={{ height: 15, width: 15, color: "#4f46e5", marginRight: 4 }}
                        />{" "}
                        Pausa
                      </th>
                      {editingShiftId === wh.id && (
                        <th className="px-4 py-2 text-gray-600 w-[20%]">Azioni</th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {(wh.time_slots || [])
                      .slice()
                      .sort((a, b) => (a?.start_at || "").localeCompare(b?.start_at || ""))
                      .map((timeSlot, slotIndex) => (
                        <tr key={timeSlot.id || slotIndex} className="text-center">
                          <td className="border px-4 py-2 font-medium">
                            {formatTime(timeSlot.start_at)} - {formatTime(timeSlot.end_at)}
                          </td>

                          <td className="border px-4 py-2">
                            {timeSlot?.breaks?.length > 0 ? (
                              <div className="space-y-1">
                                {timeSlot.breaks.map((brk, brkIdx) => (
                                  <div
                                    key={brk.id || brkIdx}
                                    className={`flex items-center justify-center space-x-1 ${
                                      !brk?.is_visible ? "text-gray-400" : "text-gray-600"
                                    }`}
                                  >
                                    {!brk?.is_visible && (
                                      <EyeSlashIcon className="h-4 w-4 text-red-500" strokeWidth={3} />
                                    )}
                                    <span>
                                      {formatTime(brk.start_at)} - {formatTime(brk.end_at)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">Nessuna</span>
                            )}
                          </td>

                          {editingShiftId === wh.id && (
                            <td className="border px-2 py-2">
                              <div className="flex justify-center space-x-2">
                                <CustomTooltipComponent title="Modifica fascia oraria" arrow>
                                  <button
                                    type="button"
                                    className="p-1"
                                    onClick={() => handleEditTimeSlot(timeSlot, idx, wh.id)}
                                  >
                                    <PencilSquareIcon className="h-5 w-5 text-indigo-800 hover:text-indigo-500" />
                                  </button>
                                </CustomTooltipComponent>

                                <CustomTooltipComponent title="Elimina" arrow>
                                  <button
                                    type="button"
                                    className="p-1"
                                    onClick={() => handleDeleteTimeSlot(timeSlot.id)}
                                  >
                                    <TrashIcon className="h-5 w-5 text-indigo-800 hover:text-red-500" />
                                  </button>
                                </CustomTooltipComponent>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>

                {editingShiftId === wh.id && (
                  <button
                    type="button"
                    onClick={() => handleAddTimeSlot(wh.id)}
                    className="mt-4 w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 uppercase"
                  >
                    + Aggiungi fascia oraria
                  </button>
                )}

                {/* DIALOG: AGGIUNTA FASCIA */}
                {addTimeSlot && workingHourId === wh.id && openCreateDialog && (
                  <Transition.Root show={openCreateDialog} as={Fragment}>
                    <Dialog className="relative" style={{ zIndex: 900 }} onClose={() => {}} static>
                      <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/40" />
                      </Transition.Child>

                      <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2">
                            <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
                              <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-indigo-50">
                                <div className="text-sm font-semibold text-indigo-700 uppercase font-semibold">Aggiungi fascia</div>
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-gray-600 hover:text-gray-900 uppercase"
                                  onClick={() => {
                                    setAddTimeSlot(false);
                                    setOpenCreateDialog(false);
                                  }}
                                >
                                  Chiudi
                                </button>
                              </div>

                        
                              <AddNewTimeSlot
                                workingHour={wh}
                                onCancelEdit={() => {
                                  setAddTimeSlot(false);
                                  setOpenCreateDialog(false);
                                }}
                                onAddTimeSlot={handleCreateTimeSlot}
                                allowHiddenBreaks={allowHiddenBreaks}
                              />
                            
                            </Dialog.Panel>
                          </Transition.Child>
                        </div>
                      </div>
                    </Dialog>
                  </Transition.Root>
                )}

                {/* DIALOG: MODIFICA FASCIA */}
                {editTimeSlot && editWhIndex === idx && openEditDialog && (
                  <Transition.Root show={openEditDialog} as={Fragment}>
                    <Dialog className="relative" style={{ zIndex: 900 }} onClose={() => {}} static>
                      <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/40" />
                      </Transition.Child>

                      <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2">
                            <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
                              <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-indigo-50">
                                <div className="text-sm font-semibold text-indigo-700 uppercase font-semibold">Modifica fascia oraria</div>
                                <button
                                  type="button"
                                  className="text-sm font-semibold text-gray-600 hover:text-gray-900 uppercase"
                                  onClick={() => {
                                    setEditTimeSlot(null);
                                    setOpenEditDialog(false);
                                  }}
                                >
                                  Chiudi
                                </button>
                              </div>

                              <EditTimeSlot
                                timeSlotObject={editTimeSlot[0]}
                                onClose={() => {
                                  setEditTimeSlot(null);
                                  setOpenEditDialog(false);
                                }}
                                onShiftPreview={() => {
                                  setEditTimeSlot(null);
                                  setOpenEditDialog(false);
                                }}
                                onCancelEdit={() => {
                                  setEditTimeSlot(null);
                                  setOpenEditDialog(false);
                                }}
                                onUpdatedTimeSlot={handleUpdatedTimeSlot}
                                timeSlots={workingHourId ? (wh?.time_slots || []) : []}
                                allowHiddenBreaks={allowHiddenBreaks}
                              />
                          
                            </Dialog.Panel>
                          </Transition.Child>
                        </div>
                      </div>
                    </Dialog>
                  </Transition.Root>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

